/**
 * Compliance form-effect — sets body attributes and injects link elements
 * for GDPR, HIPAA, audit trail, privacy policy, and terms of service.
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str } from './utils';

export const complianceEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const created: Element[] = [];

  // GDPR consent
  if (config.gdprConsentEnabled === true) {
    document.body.setAttribute('data-atlas-gdpr', 'true');
    const gdprText = str(config.gdprConsentText);
    if (gdprText) {
      document.body.setAttribute('data-atlas-gdpr-text', gdprText);
    }
  }

  // HIPAA mode
  if (config.hipaaMode === true) {
    document.body.setAttribute('data-atlas-hipaa', 'true');
  }

  // Audit trail
  if (config.auditTrailEnabled === true) {
    document.body.setAttribute('data-atlas-audit', 'true');
  }

  // Privacy policy link
  const privacyUrl = str(config.privacyPolicyUrl);
  if (privacyUrl) {
    const link = document.createElement('link');
    link.rel = 'privacy-policy';
    link.href = privacyUrl;
    document.head.appendChild(link);
    created.push(link);
  }

  // Terms of service link
  const termsUrl = str(config.termsUrl);
  if (termsUrl) {
    const link = document.createElement('link');
    link.rel = 'terms-of-service';
    link.href = termsUrl;
    document.head.appendChild(link);
    created.push(link);
  }

  return () => {
    for (const el of created) {
      el.parentNode?.removeChild(el);
    }
    document.body.removeAttribute('data-atlas-gdpr');
    document.body.removeAttribute('data-atlas-gdpr-text');
    document.body.removeAttribute('data-atlas-hipaa');
    document.body.removeAttribute('data-atlas-audit');
  };
};
